import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, CalendarCheck, Star, BadgeCheck, Truck } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, HERO_IMAGE } from "@/constants/site";
import { scrollToId } from "@/lib/scroll";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#FAFAFA] pb-16 pt-36 sm:pt-40 lg:pb-24 lg:pt-48">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#0F2B48 1px, transparent 1px), linear-gradient(90deg, #0F2B48 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-rescue"
            data-testid="hero-eyebrow"
          >
            <BadgeCheck className="h-3.5 w-3.5" />
            Fast. Reliable. Done Right.
          </motion.div>

          <h1 className="font-display text-4xl font-black leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-7xl">
            <Line delay={0.1}>Toronto's #1</Line>
            <Line delay={0.22}>Emergency Appliance</Line>
            <Line delay={0.34}>
              Repair <span className="text-rescue">Done Today.</span>
            </Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Certified local technicians at your door today — refrigerators, washers, dryers,
            dishwashers, stoves, freezers & microwaves. Serving Toronto and the entire GTA.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-7"
          >
            <div
              data-testid="service-fee-waived-badge"
              className="inline-flex -rotate-1 items-center gap-2.5 rounded-full bg-gradient-to-r from-rescue to-red-500 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-wider text-white shadow-xl shadow-red-600/30 sm:text-sm"
            >
              <BadgeCheck className="h-4 w-4 shrink-0" />
              $80 Service Call — Waived When You Proceed With the Repair
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <a
              data-testid="hero-call-now-button"
              href={PHONE_TEL}
              className="group flex items-center justify-center gap-2.5 rounded-full bg-rescue px-8 py-4 text-base font-bold text-white shadow-xl shadow-red-600/30 transition-[background-color,transform] duration-200 hover:bg-rescue-dark hover:-translate-y-0.5"
            >
              <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
              Call Now: {PHONE_DISPLAY}
            </a>
            <button
              data-testid="hero-request-service-button"
              onClick={() => scrollToId("request-service")}
              className="flex items-center justify-center gap-2.5 rounded-full border-2 border-navy bg-white px-8 py-4 text-base font-bold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            >
              <CalendarCheck className="h-5 w-5" />
              Request Service Online
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold text-slate-600"
            data-testid="hero-trust-row"
          >
            <span className="flex items-center gap-1.5">
              <span className="flex text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </span>
              4.9 — 1,480+ GTA homeowners served
            </span>
            <span className="flex items-center gap-1.5">
              <BadgeCheck className="h-4 w-4 text-rescue" /> 1-Yr Parts • 30-Day Labour Warranty
            </span>
            <span className="flex items-center gap-1.5">
              <Truck className="h-4 w-4 text-rescue" /> Licensed & Insured
            </span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -right-4 -top-4 h-full w-full rounded-[2rem] border-2 border-rescue/30" />
            <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-navy/25">
              <motion.img
                src={HERO_IMAGE}
                alt="Modern kitchen appliances repaired by GTA Appliance Rescue"
                style={{ y: imgY }}
                className="h-[420px] w-full scale-110 object-cover sm:h-[520px]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="absolute -left-3 bottom-6 rounded-2xl border border-white/40 bg-white/85 p-4 shadow-xl backdrop-blur-xl sm:-left-8"
              data-testid="hero-dispatch-card"
            >
              <div className="flex items-center gap-3">
                <span className="pulse-dot h-2.5 w-2.5 rounded-full bg-green-500" />
                <div>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                    Live Dispatch
                  </p>
                  <p className="text-sm font-bold text-navy">Technicians available near you today</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
